import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CallSubStatusDetail,
  type CallSubStatusListItem,
  CreateCallSubStatusRequestSchema,
  type CreateCallSubStatusRequest,
  UpdateCallSubStatusRequestSchema,
  type UpdateCallSubStatusRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm, useFormContext } from 'react-hook-form';

import { useCreateCallSubStatus, useUpdateCallSubStatus } from '../use-call-sub-statuses';

import { ReasonsPicker } from './ReasonsPicker';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { useCallStatusesList } from '@/features/call-statuses/use-call-statuses';
import { useFollowupReasonsList } from '@/features/followup-reasons/use-followup-reasons';
import { useLostReasonsList } from '@/features/lost-reasons/use-lost-reasons';

interface CallSubStatusFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  callSubStatus?: CallSubStatusDetail | CallSubStatusListItem | null;
}

const CREATE_DEFAULTS: CreateCallSubStatusRequest = {
  callStatusId: '',
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  // ADR-0051 + 0053. All three follow-up behavior flags default
  // `false` so a brand-new sub-status is inert until the operator
  // opts in - matches the column-default in the migration.
  isAutoFollowup: false,
  isRequireFollowUp: false,
  isLostReason: false,
  defaultFollowupReasonId: null,
  // Empty = unrestricted (dialog shows every active reason).
  followupReasonIds: [],
  lostReasonIds: [],
  isActive: true,
};

const PARENT_LIST_QUERY = {
  page: 1 as const,
  pageSize: 200 as const,
  sortDir: 'asc' as const,
  sortBy: 'sortOrder',
  isActive: true,
};

/**
 * Field set shared by create + edit. The parent picker is driven
 * by the CallStatus master list (no `/options` endpoint yet, but
 * with ~10-20 parents in practice a single pageSize=200 fetch is
 * cheap and shares cache with everywhere else CallStatus is read).
 */
const CallSubStatusFields = ({
  pending,
  parentOptions,
  followupReasonOptions,
  lostReasonOptions,
  isAutoFollowup,
  isRequireFollowUp,
  isLostReason,
}: {
  pending: boolean;
  parentOptions: ReadonlyArray<{ value: string; label: string }>;
  followupReasonOptions: ReadonlyArray<{ value: string; label: string }>;
  lostReasonOptions: ReadonlyArray<{ value: string; label: string }>;
  isAutoFollowup: boolean;
  isRequireFollowUp: boolean;
  isLostReason: boolean;
}): React.JSX.Element => {
  // Both forms are rendered inside <UzForm>, which is a FormProvider - so the
  // exclusivity rule below can clear sibling fields without the parent having
  // to hand down a create-vs-update specific setter.
  const { setValue } = useFormContext();

  // "Mark lead as Lost" closes the lead, so scheduling the next attempt or
  // demanding a next-followup datetime on the same sub-status contradicts it
  // (the server already short-circuits the auto chain when Lost wins). The two
  // follow-up toggles are therefore forced OFF and locked while Lost is ON.
  //
  // Runs as an effect rather than inside Lost's onCheckedChange so it also
  // normalises rows saved BEFORE this rule existed: opening such a record in
  // edit mode clears the conflicting flags, and saving persists the fix.
  useEffect(() => {
    if (!isLostReason) return;
    if (isAutoFollowup) setValue('isAutoFollowup', false, { shouldDirty: true });
    if (isRequireFollowUp) setValue('isRequireFollowUp', false, { shouldDirty: true });
  }, [isLostReason, isAutoFollowup, isRequireFollowUp, setValue]);

  return (
    <>
      <UzFormField name="callStatusId">
        {({ field, error }) => (
          <UzSelect
            label="Call status"
            placeholder="Select parent call status"
            value={(field.value as string | undefined) ?? ''}
            onValueChange={field.onChange}
            options={parentOptions}
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="e.g. Reached and quoted"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="description">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Description"
            placeholder="Optional"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      {/* Follow-up behavior flags (ADR-0051 + 0053). All three control
        what the complete-followup dialog renders + what the server
        does on submit. The valid combinations are:
          - isAutoFollowup ON only:  SOP-chained calls (e.g. callback)
          - isAutoFollowup + isRequireFollowUp: SOP-chained + reason
            audit (e.g. not-connected outcomes)
          - isLostReason ON only:    closes the lead with a chosen
            lost reason (e.g. not interested)
        Lost is EXCLUSIVE with the other two - see the effect above. */}
      <div className="border-t border-zinc-200 pt-3">
        <div className="mb-2 text-xs font-semibold tracking-wide text-zinc-500 uppercase">
          Follow-up behavior
        </div>
        <div className="space-y-3">
          <UzFormField name="isAutoFollowup">
            {({ field }) => (
              <UzSwitch
                label="Auto-followup"
                description={
                  isLostReason
                    ? 'Not available while "Mark lead as Lost" is on - a sub status that closes the lead cannot schedule the next attempt.'
                    : 'Schedule the next attempt automatically (7 daily retries at 19:00 IST, ≥24h apart). Operators log the outcome on the scheduled row - no manual next-date picker.'
                }
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                disabled={pending || isLostReason}
              />
            )}
          </UzFormField>
          {isAutoFollowup && (
            <UzFormField name="defaultFollowupReasonId">
              {({ field, error }) => (
                <UzSelect
                  label="Default follow-up reason"
                  placeholder="Select a reason for auto chains"
                  value={(field.value as string | null | undefined) ?? undefined}
                  onValueChange={(v) => field.onChange(v ?? null)}
                  options={followupReasonOptions}
                  error={error}
                  disabled={pending || followupReasonOptions.length === 0}
                  hint="Auto-filled on the completed follow-up row when the operator does not pick a reason."
                />
              )}
            </UzFormField>
          )}
          <UzFormField name="isRequireFollowUp">
            {({ field }) => (
              <UzSwitch
                label="Require follow-up"
                description={
                  isLostReason
                    ? 'Not available while "Mark lead as Lost" is on - the lead is closed on submit, so there is no next follow-up to demand.'
                    : 'When ON, the complete-followup dialog shows the Followup Reason dropdown AND requires a future next-followup datetime before the operator can submit.'
                }
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                disabled={pending || isLostReason}
              />
            )}
          </UzFormField>
          <UzFormField name="isLostReason">
            {({ field }) => (
              <UzSwitch
                label="Mark lead as Lost"
                description="Show the Lost reason dropdown on the complete-followup dialog and auto-close the lead to LOST with the chosen reason on submit."
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                disabled={pending}
              />
            )}
          </UzFormField>
          {/* Only meaningful when the sub-status closes the lead, so it
            follows the same show-on-toggle pattern as the default
            follow-up reason under Auto-followup. */}
          {isLostReason && (
            <UzFormField name="lostReasonIds">
              {({ field }) => (
                <ReasonsPicker
                  label="Lost reasons"
                  description="Lost reasons the operator can pick when this sub status closes the lead. Leave empty to allow every active lost reason."
                  searchPlaceholder="Search lost reasons…"
                  emptyResultsLabel="No lost reasons found."
                  unrestrictedLabel="All lost reasons allowed"
                  value={(field.value as string[] | undefined) ?? []}
                  onChange={field.onChange}
                  options={lostReasonOptions}
                  disabled={pending}
                />
              )}
            </UzFormField>
          )}
          {/* A sub-status that closes the lead never renders the follow-up
            reason dropdown (the server short-circuits the auto-followup
            helper when isLostReason is on), so the mapping is dead UI
            here. Hidden rather than cleared: the stored mapping stays put
            and reappears if the operator turns Lost back off. */}
          {!isLostReason && (
            <UzFormField name="followupReasonIds">
              {({ field }) => (
                <ReasonsPicker
                  label="Follow-up reasons"
                  description="Reasons the operator can pick in the complete-follow-up dialog when this sub status is chosen. Leave empty to allow every active reason."
                  searchPlaceholder="Search reasons…"
                  emptyResultsLabel="No follow-up reasons found."
                  unrestrictedLabel="All reasons allowed"
                  value={(field.value as string[] | undefined) ?? []}
                  onChange={field.onChange}
                  options={followupReasonOptions}
                  disabled={pending}
                />
              )}
            </UzFormField>
          )}
        </div>
      </div>
      <UzFormField name="isActive">
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Inactive sub statuses are hidden from follow-up forms."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

export const CallSubStatusFormDrawer = ({
  open,
  onOpenChange,
  mode,
  callSubStatus,
}: CallSubStatusFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCallSubStatus = useCreateCallSubStatus();
  const updateCallSubStatus = useUpdateCallSubStatus();
  const pending = createCallSubStatus.isPending || updateCallSubStatus.isPending;

  const parentQuery = useCallStatusesList(PARENT_LIST_QUERY);
  const parentOptions: ReadonlyArray<{ value: string; label: string }> = (
    parentQuery.data?.data ?? []
  ).map((s) => ({ value: s.id, label: s.name }));

  const followupReasonsQuery = useFollowupReasonsList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const followupReasonOptions = (followupReasonsQuery.data?.data ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const lostReasonsQuery = useLostReasonsList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const lostReasonOptions = (lostReasonsQuery.data?.data ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const createForm = useForm<CreateCallSubStatusRequest>({
    resolver: zodResolver(CreateCallSubStatusRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCallSubStatusRequest>({
    resolver: zodResolver(UpdateCallSubStatusRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (callSubStatus) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        callStatusId: callSubStatus.callStatus.id,
        name: callSubStatus.name,
        description: callSubStatus.description ?? '',
        sortOrder: callSubStatus.sortOrder,
        // ADR-0051 + 0053 follow-up behavior flags hydrate the
        // three toggles in edit mode so admins see the current
        // state before changing it.
        isAutoFollowup: callSubStatus.isAutoFollowup,
        isRequireFollowUp: callSubStatus.isRequireFollowUp,
        isLostReason: callSubStatus.isLostReason,
        defaultFollowupReasonId: callSubStatus.defaultFollowupReasonId ?? null,
        followupReasonIds: (callSubStatus.followupReasons ?? []).map((r) => r.id),
        lostReasonIds: (callSubStatus.lostReasons ?? []).map((r) => r.id),
        isActive: callSubStatus.isActive,
      });
    }
  }, [open, isCreate, callSubStatus, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createCallSubStatus.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!callSubStatus) return;
    await updateCallSubStatus.mutateAsync({
      id: callSubStatus.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = (
    <CallSubStatusFields
      pending={pending}
      parentOptions={parentOptions}
      followupReasonOptions={followupReasonOptions}
      lostReasonOptions={lostReasonOptions}
      isAutoFollowup={Boolean(
        isCreate ? createForm.watch('isAutoFollowup') : updateForm.watch('isAutoFollowup'),
      )}
      isRequireFollowUp={Boolean(
        isCreate ? createForm.watch('isRequireFollowUp') : updateForm.watch('isRequireFollowUp'),
      )}
      isLostReason={Boolean(
        isCreate ? createForm.watch('isLostReason') : updateForm.watch('isLostReason'),
      )}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add call sub status' : `Edit ${callSubStatus?.name ?? 'call sub status'}`}
      description="Code is unique within the selected parent call status and serves as the stable machine identifier."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
